import type { EndpointConfig } from '../types'

export const ReportServiceEndpoints = {
  CATALOG_GET: 'report-service-get-catalog',
  LIMITS_GET: 'report-service-get-limits',
  PRESET_PREVIEW: 'report-service-preset-preview',
  DEFINITIONS_LIST: 'report-service-list-definitions',
  DEFINITIONS_CREATE: 'report-service-create-definition',
  DEFINITIONS_GET: 'report-service-get-definition',
  DEFINITIONS_UPDATE: 'report-service-update-definition',
  DEFINITIONS_DELETE: 'report-service-delete-definition',
  DEFINITIONS_DUPLICATE_FROM_PRESET: 'report-service-duplicate-definition-from-preset',
  DEFINITION_PREVIEW: 'report-service-definition-preview',
  DEFINITION_DRAFT_PREVIEW: 'report-service-definition-draft-preview',
  RUNS_CREATE: 'report-service-create-run',
  RUNS_LIST: 'report-service-list-runs',
  RUNS_GET: 'report-service-get-run',
  RUNS_CANCEL: 'report-service-cancel-run',
  RUNS_ARTEFACT: 'report-service-get-run-artefact',
  SCHEDULES_LIST: 'report-service-list-schedules',
  SCHEDULES_CREATE: 'report-service-create-schedule',
  SCHEDULES_GET: 'report-service-get-schedule',
  SCHEDULES_UPDATE: 'report-service-update-schedule',
  SCHEDULES_DELETE: 'report-service-delete-schedule',
  SCHEDULES_RUN_NOW: 'report-service-run-schedule-now',
  QUERY: 'report-service-query',
  DRILL: 'report-service-drill',
  CAPABILITIES_GET: 'report-service-get-capabilities',
  DASHBOARDS_LIST: 'report-service-list-dashboards',
  DASHBOARDS_CREATE: 'report-service-create-dashboard',
  DASHBOARDS_DEFAULT_GET: 'report-service-get-default-dashboard',
  DASHBOARDS_DEFAULT_SET: 'report-service-set-default-dashboard',
  DASHBOARDS_LICENCE_DEFAULT_SET: 'report-service-set-licence-default-dashboard',
  DASHBOARDS_DUPLICATE_FROM_TEMPLATE: 'report-service-duplicate-dashboard-from-template',
  DASHBOARDS_GET: 'report-service-get-dashboard',
  DASHBOARDS_UPDATE: 'report-service-update-dashboard',
  DASHBOARDS_DELETE: 'report-service-delete-dashboard',
  DASHBOARDS_LAYOUT_UPDATE: 'report-service-update-dashboard-layout',
  DASHBOARDS_DUPLICATE: 'report-service-duplicate-dashboard',
  DASHBOARD_WIDGETS_CREATE: 'report-service-create-dashboard-widget',
  DASHBOARD_WIDGETS_UPDATE: 'report-service-update-dashboard-widget',
  DASHBOARD_WIDGETS_DELETE: 'report-service-delete-dashboard-widget'
} as const

type EndpointKey = keyof typeof ReportServiceEndpoints
type EndpointId = (typeof ReportServiceEndpoints)[EndpointKey]

export const reportServiceEndpointsConfig: Record<EndpointId, EndpointConfig> = {
  'report-service-get-catalog': {
    method: 'GET',
    path: '/reports/catalog',
    description: 'Presets, columns and filters available to the licence'
  },
  'report-service-get-limits': {
    method: 'GET',
    path: '/reports/limits',
    description: 'Effective export and window limits of the caller licence'
  },
  'report-service-preset-preview': {
    method: 'POST',
    path: '/reports/presets/:key/preview',
    description: 'Synchronous preview page of a preset report'
  },
  'report-service-list-definitions': {
    method: 'GET',
    path: '/reports/definitions',
    description: 'Report definitions visible to the caller, optionally with the locked templates'
  },
  'report-service-create-definition': {
    method: 'POST',
    path: '/reports/definitions',
    description: 'Create a report definition from a preset or from a grain'
  },
  'report-service-get-definition': {
    method: 'GET',
    path: '/reports/definitions/:id',
    description: 'One report definition; a tpl: id answers a locked template'
  },
  'report-service-update-definition': {
    method: 'PUT',
    path: '/reports/definitions/:id',
    description: 'Update a report definition; a locked definition answers 409'
  },
  'report-service-delete-definition': {
    method: 'DELETE',
    path: '/reports/definitions/:id',
    description: 'Delete a report definition; a locked definition answers 409'
  },
  'report-service-duplicate-definition-from-preset': {
    method: 'POST',
    path: '/reports/definitions/duplicate-from-preset/:key',
    description: 'Copy a preset template into an editable definition'
  },
  'report-service-definition-preview': {
    method: 'POST',
    path: '/reports/definitions/:id/preview',
    description: 'Synchronous preview page of a stored definition'
  },
  'report-service-definition-draft-preview': {
    method: 'POST',
    path: '/reports/definitions/preview',
    description: 'Synchronous preview page of an unsaved definition draft'
  },
  'report-service-create-run': {
    method: 'POST',
    path: '/reports/runs',
    description: 'Enqueue an asynchronous export run'
  },
  'report-service-list-runs': {
    method: 'GET',
    path: '/reports/runs',
    description: 'Recent runs visible to the caller'
  },
  'report-service-get-run': {
    method: 'GET',
    path: '/reports/runs/:id',
    description: 'One run with its status and artefacts'
  },
  'report-service-cancel-run': {
    method: 'DELETE',
    path: '/reports/runs/:id',
    description: 'Cancel a queued run; a run that already started or finished answers 409'
  },
  'report-service-get-run-artefact': {
    method: 'GET',
    path: '/reports/runs/:id/artefacts/:format',
    description: 'Signed download link of a run artefact'
  },
  'report-service-list-schedules': {
    method: 'GET',
    path: '/reports/schedules',
    description: 'Schedules visible to the caller'
  },
  'report-service-create-schedule': {
    method: 'POST',
    path: '/reports/schedules',
    description: 'Create a schedule'
  },
  'report-service-get-schedule': {
    method: 'GET',
    path: '/reports/schedules/:id',
    description: 'One schedule'
  },
  'report-service-update-schedule': {
    method: 'PUT',
    path: '/reports/schedules/:id',
    description: 'Update a schedule'
  },
  'report-service-delete-schedule': {
    method: 'DELETE',
    path: '/reports/schedules/:id',
    description: 'Delete a schedule'
  },
  'report-service-run-schedule-now': {
    method: 'POST',
    path: '/reports/schedules/:id/run-now',
    description: 'Enqueue a run of the schedule immediately'
  },
  'report-service-query': {
    method: 'POST',
    path: '/reports/query',
    description: 'Answer up to sixteen definition or inline spec queries in one round trip'
  },
  'report-service-drill': {
    method: 'POST',
    path: '/reports/drill',
    description: 'Turn a summary cell into the detail spec and params behind it'
  },
  'report-service-get-capabilities': {
    method: 'GET',
    path: '/reports/capabilities',
    description: 'Reports and dashboards capabilities of the caller licence'
  },
  'report-service-list-dashboards': {
    method: 'GET',
    path: '/dashboards',
    description: 'Dashboards visible to the caller, optionally with the locked templates'
  },
  'report-service-create-dashboard': {
    method: 'POST',
    path: '/dashboards',
    description: 'Create a dashboard'
  },
  'report-service-get-default-dashboard': {
    method: 'GET',
    path: '/dashboards/default',
    description: 'Resolved default dashboard, user override first then licence default'
  },
  'report-service-set-default-dashboard': {
    method: 'PUT',
    path: '/dashboards/default',
    description: 'Set or clear the caller default dashboard'
  },
  'report-service-set-licence-default-dashboard': {
    method: 'PUT',
    path: '/dashboards/licence-default',
    description: 'Set or clear the licence default dashboard'
  },
  'report-service-duplicate-dashboard-from-template': {
    method: 'POST',
    path: '/dashboards/duplicate-from-template/:key',
    description: 'Copy a seeded dashboard template into an editable dashboard'
  },
  'report-service-get-dashboard': {
    method: 'GET',
    path: '/dashboards/:id',
    description: 'One dashboard with its widgets in layout order'
  },
  'report-service-update-dashboard': {
    method: 'PUT',
    path: '/dashboards/:id',
    description: 'Update a dashboard name, description, share level and page filters'
  },
  'report-service-delete-dashboard': {
    method: 'DELETE',
    path: '/dashboards/:id',
    description: 'Delete a dashboard; a locked template answers 409'
  },
  'report-service-update-dashboard-layout': {
    method: 'PUT',
    path: '/dashboards/:id/layout',
    description: 'Move and resize the widgets of a dashboard in one call'
  },
  'report-service-duplicate-dashboard': {
    method: 'POST',
    path: '/dashboards/:id/duplicate',
    description: 'Copy a dashboard into the caller licence as private'
  },
  'report-service-create-dashboard-widget': {
    method: 'POST',
    path: '/dashboards/:id/widgets',
    description: 'Add a widget to a dashboard'
  },
  'report-service-update-dashboard-widget': {
    method: 'PUT',
    path: '/dashboards/:id/widgets/:widgetId',
    description: 'Update a widget of a dashboard'
  },
  'report-service-delete-dashboard-widget': {
    method: 'DELETE',
    path: '/dashboards/:id/widgets/:widgetId',
    description: 'Remove a widget from a dashboard'
  }
}
